import { useEffect, useRef, useState } from 'react';
import { Button, Divider, Form, FormInstance, Input, InputNumber, Slider } from 'antd';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';

import { txt } from '../../../../locales/i18n';
import { AnimationCollapse } from './animation';

export const ToggleButton = (props: { propertyKey: string[]; icon: IconDefinition }) => {
	const checkboxRef = useRef<HTMLInputElement>(null);
	const buttomRef = useRef<HTMLButtonElement>(null);
	const [checked, setChecked] = useState(false);

	useEffect(() => {
		const checkbox = checkboxRef.current;
		if (checkbox === null) return;

		setChecked(checkbox.checked);

		const handleChange = (event: any) => {
			const checked = event?.target?.checked ?? false;
			setChecked(checked);
		};

		checkbox.addEventListener('change', handleChange);
		return () => checkbox.removeEventListener('change', handleChange);
	}, []);

	const handleToggleCheckbox = () => {
		const checkbox = checkboxRef.current;
		checkbox?.click();

		const button = buttomRef.current;
		button?.blur();
	};

	return (
		<div>
			<Form.Item name={props.propertyKey} valuePropName="checked" noStyle>
				<input type="checkbox" ref={checkboxRef} style={{ display: 'none' }} />
			</Form.Item>

			<Button
				ref={buttomRef}
				ghost={checked}
				onClick={handleToggleCheckbox}
				type={checked ? 'primary' : 'default'}
			>
				<FontAwesomeIcon icon={props.icon} />
			</Button>
		</div>
	);
};

export type SignupFormPanelProps = {
	propertyKey: string;
	formRef: React.RefObject<FormInstance>;
	onSectionChange: (changedValues: any, values: any) => void;
};

export const SignupFormPanel = (props: SignupFormPanelProps) => {
	return (
		<TextPanelStyled>
			<div className="row-container">
				<div>
					<label>{txt('cms.color')}</label>
					<Form.Item name={[props.propertyKey, 'backgroundColor']} noStyle>
						<Input type="color" />
					</Form.Item>
				</div>

				<div className="form-item-container">
					<label>{txt('cms.opacity')}</label>

					<div style={{ display: 'flex' }}>
						<Form.Item name={[props.propertyKey, 'backgroundOpacity']} noStyle>
							<Slider min={0} max={255} style={{ flex: 1 }} />
						</Form.Item>

						<Form.Item name={[props.propertyKey, 'backgroundOpacity']}>
							<InputNumber
								bordered={false}
								size="small"
								min={0}
								max={255}
								style={{ width: '6ch' }}
							/>
						</Form.Item>
					</div>
				</div>
			</div>

			<div className="form-item-container">
				<label>{txt('cms.gradient')}</label>
				<Form.Item name={[props.propertyKey, 'backgroundGradient']}>
					<Input />
				</Form.Item>
			</div>

			<div className="form-item-container">
				<label>{txt('cms.blur')}</label>
				<div style={{ display: 'flex' }}>
					<Form.Item name={[props.propertyKey, 'backgroundBlur']} noStyle>
						<Slider min={0} max={50} style={{ flex: 1 }} />
					</Form.Item>

					<Form.Item name={[props.propertyKey, 'backgroundBlur']}>
						<InputNumber
							bordered={false}
							size="small"
							min={0}
							max={50}
							style={{ width: '6ch' }}
						/>
					</Form.Item>
				</div>
			</div>

			<label>
				<b>{txt('cms.text')}</b>
			</label>

			<div>
				<label>{txt('cms.title')}</label>
				<Form.Item noStyle name={[props.propertyKey, 'titleText']}>
					<Input allowClear placeholder={txt('cms.text')} />
				</Form.Item>
			</div>

			<div>
				<label>{txt('cms.button')}</label>
				<Form.Item noStyle name={[props.propertyKey, 'buttonText']}>
					<Input allowClear placeholder={txt('cms.text')} />
				</Form.Item>
			</div>

			<Divider />

			<div className="flex-vertical">
				<div>
					<label>
						<b>{txt('cms.text')}</b>
					</label>

					<div className="row-container">
						<div>
							<label>{txt('cms.text')}</label>
							<Form.Item name={[props.propertyKey, 'textColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>

						<div>
							<label>{txt('cms.header')}</label>
							<Form.Item name={[props.propertyKey, 'headerTextColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>

						<div>
							<label>{txt('cms.link')}</label>
							<Form.Item name={[props.propertyKey, 'linkTextColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>
					</div>
				</div>

				<div>
					<label>
						<b>{txt('cms.input')}</b>
					</label>

					<div className="row-container">
						<div>
							<label>{txt('cms.text')}</label>
							<Form.Item name={[props.propertyKey, 'inputTextColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>

						<div>
							<label>{txt('cms.background')}</label>
							<Form.Item name={[props.propertyKey, 'inputBackgroundColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>

						<div>
							<label>{txt('cms.placeholder')}</label>
							<Form.Item name={[props.propertyKey, 'placeholderTextColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>
					</div>
				</div>

				<div>
					<label>
						<b>{txt('cms.button')}</b>
					</label>

					<div className="row-container">
						<div>
							<label>{txt('cms.text')}</label>
							<Form.Item name={[props.propertyKey, 'buttonTextColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>

						<div>
							<label>{txt('cms.background')}</label>
							<Form.Item name={[props.propertyKey, 'buttonBackgroundColor']} noStyle>
								<Input type="color" />
							</Form.Item>
						</div>
					</div>
				</div>
			</div>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>
		</TextPanelStyled>
	);
};

const TextPanelStyled = styled.div`
	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}

	.flex-vertical {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;
